import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import useConversations from '../../hooks/useConversations';
import { useMessagesUI } from '../../contexts/MessagesUIContext';
import '../../styles/PatientMessaging.css';

const AskAssistantButton = ({
  patient,
  admission = null,
  placement = 'patient-card',
  buttonClassName = 'patient-messaging-btn ask-assistant-btn',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const { hasPermission } = useAuth();
  const { sendMessage, fetchAssistantThread } = useConversations();
  const { openMessages } = useMessagesUI();

  const canSend = hasPermission ? hasPermission('/messaging/send', 'Execute') : false;
  const canAsk = hasPermission ? hasPermission('/ai-assist/messaging-agent', 'Execute') : false;

  const objectType = placement === 'admission-card' ? 'admission' : 'patient';
  const objectId = placement === 'admission-card'
    ? admission?.admission_id
    : patient?.patient_id;

  const title = placement === 'admission-card'
    ? 'Ask assistant about this admission'
    : 'Ask assistant about this patient';

  if (!patient || !canSend || !canAsk || !objectId) {
    return null;
  }

  const closeModal = () => {
    setMessage('');
    setIsOpen(false);
  };

  const handleOpen = async (e) => {
    e?.stopPropagation?.();
    setOpening(true);
    try {
      const result = await fetchAssistantThread({ objectType, objectId });
      if (result?.conversation?.id) {
        openMessages({ conversationId: result.conversation.id });
        return;
      }
      setIsOpen(true);
    } catch (err) {
      console.error('Failed to open assistant thread:', err);
      alert(err.response?.data?.error || 'Unable to open the Clinical Assistant.');
    } finally {
      setOpening(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      alert('Please enter a question for the assistant.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await fetchAssistantThread({ objectType, objectId });
      const recipientId = result?.assistant?.id;
      if (!recipientId) {
        throw new Error('Clinical Assistant is not available');
      }
      const sent = await sendMessage({
        recipientId,
        objectType,
        objectId,
        body: message,
      });
      closeModal();
      if (sent.conversation?.id) {
        openMessages({ conversationId: sent.conversation.id });
      }
    } catch (err) {
      console.error('Failed to ask assistant:', err);
      alert(err.response?.data?.error || err.message || 'Failed to send. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        className={buttonClassName}
        onClick={handleOpen}
        title={title}
        type="button"
        disabled={opening}
      >
        <FontAwesomeIcon icon={faRobot} />
      </button>

      {isOpen && (
        <div className="patient-messaging-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="patient-messaging-modal">
            <div className="patient-messaging-header">
              <h3>{title}</h3>
              <button
                className="patient-messaging-close"
                onClick={closeModal}
                aria-label="Close"
                type="button"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="patient-messaging-content">
              <div className="patient-info-section">
                <h4>{placement === 'admission-card' ? 'Admission' : 'Patient'}</h4>
                <p>
                  <strong>Name:</strong> {patient.patient_first_name} {patient.patient_last_name}
                </p>
                <p><strong>Patient Number:</strong> {patient.patient_number}</p>
                {placement === 'admission-card' && admission && (
                  <p><strong>Admission ID:</strong> {admission.admission_id}</p>
                )}
                <p className="ask-assistant-disclaimer">
                  The assistant drafts answers from the chart. Confirm anything important in the record before acting.
                </p>
              </div>

              <form onSubmit={handleSend} className="messaging-form">
                <div className="form-group">
                  <label htmlFor="assistant-message-text">Your question:</label>
                  <textarea
                    id="assistant-message-text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ask for a summary, lab review, or a draft note..."
                    rows="4"
                    required
                  />
                </div>
                <div className="messaging-actions">
                  <button type="button" onClick={closeModal} className="btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !message.trim()}
                    className="btn-send"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {isLoading ? ' Sending...' : ' Ask assistant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AskAssistantButton;
